export type BrokerCardFacts = {
  platforms: string;
  accounts: string;
  cost: string;
  funding: string;
};

export type BrokerFact = {
  label: string;
  value: string;
  pending: boolean;
};

export type BrokerDetail = {
  brand: string[];
  regulator: BrokerFact;
  entities: string[];
  accounts: string;
  platforms: string;
  cost: string;
  funding: string;
  cautions: string[];
};

export type BrokerPlatformType = 'mt4mt5' | 'own-platform';

export type BrokerCostModel = 'spread' | 'commission';

export type BrokerFilterOption = {
  value: string;
  label: string;
};

export type Broker = {
  id: string;
  anchor: string;
  name: string;
  mark: string;
  /** 经纪商 logo 图片路径（public/ 目录下的静态资源） */
  logo: string;
  tagline: string;
  summary: string;
  /** Platform types the broker actually lists on this page. */
  platformTypes: BrokerPlatformType[];
  /** Cost models the broker's account list covers. */
  costModels: BrokerCostModel[];
  card: BrokerCardFacts;
  detail: BrokerDetail;
  /** Verified official website. Leave empty until the link has been checked. */
  officialUrl: string;
  /** Verified official account-opening link. Leave empty until the link has been checked. */
  accountUrl: string;
};

export const brokerRecommendation = {
  eyebrow: 'เลือกโบรกเกอร์',
  title: 'เลือกโบรกเกอร์ Forex ก่อนเริ่มเทรด',
  subtitle:
    'เปรียบเทียบข้อมูลบัญชี แพลตฟอร์มการเทรด ค่าใช้จ่าย และเงื่อนไขต่าง ๆ ก่อนตัดสินใจเปิดบัญชี',
  statusNote:
    'ตัวเลขในหน้านี้ยังไม่ได้รับการยืนยันจากเว็บไซต์ทางการของแต่ละโบรกเกอร์ ช่องที่ขึ้นว่า รอยืนยัน จะถูกเติมให้ครบหลังตรวจสอบกับเอกสารของโบรกเกอร์แล้ว ระหว่างนี้ใช้เป็นโครงเพื่อเทียบหัวข้อที่ต้องถามก่อนสมัคร',
  pendingLabel: 'รอยืนยัน',
  cardFactLabels: {
    platforms: 'แพลตฟอร์ม',
    accounts: 'ประเภทบัญชี',
    cost: 'ต้นทุนการเทรด',
    funding: 'ฝาก–ถอน',
  },
  detailTitle: 'ข้อมูลแต่ละโบรกเกอร์อย่างละเอียด',
  detailSubtitle:
    'อ่านทีละหัวข้อแล้วเทียบกับเงื่อนไขของประเทศคุณเอง เพราะโบรกเกอร์เดียวกันอาจให้บริการผ่านคนละนิติบุคคลในแต่ละภูมิภาค',
  viewDetail: 'ดูรายละเอียด',
  openAccount: 'เปิดบัญชี',
  linkPending: 'ลิงก์ทางการยังไม่แสดง',
  linkPendingNote:
    'ลิงก์เว็บไซต์และลิงก์เปิดบัญชีของโบรกเกอร์นี้ยังไม่ได้รับการตรวจสอบ จึงยังไม่แสดงบนหน้าเว็บ เพื่อไม่ให้ผู้อ่านถูกพาไปยังปลายทางที่ไม่ถูกต้อง',
  detailFactTitles: {
    regulator: 'ผู้กำกับดูแลและนิติบุคคล',
    accounts: 'ประเภทบัญชี',
    platforms: 'แพลตฟอร์มการเทรด',
    cost: 'สเปรด ค่าคอมมิชชัน และค่าธรรมเนียม',
    funding: 'การฝากและถอนเงิน',
  },
  disclosureTitle: 'การเปิดเผยความสัมพันธ์ทางการตลาด',
  disclosureText:
    'หน้าเว็บนี้อาจมีลิงก์พันธมิตร (affiliate) ซึ่งหมายความว่าเว็บไซต์อาจได้รับค่าตอบแทนเมื่อผู้อ่านสมัครหรือเปิดบัญชีผ่านลิงก์เหล่านั้น ค่าตอบแทนดังกล่าวไม่มีผลต่อหัวข้อและเนื้อหาที่เราเลือกนำเสนอ และไม่มีผลต่อต้นทุนที่ผู้อ่านจ่ายให้โบรกเกอร์โดยตรง',
  riskTitle: 'ข้อควรระวังก่อนเปิดบัญชี',
  riskText:
    'การเทรด Forex และผลิตภัณฑ์ที่มีเลเวอเรจมีความเสี่ยงสูง ผู้อ่านอาจสูญเสียเงินมากกว่าที่วางแผนไว้ และไม่ควรใช้เงินที่จำเป็นต่อชีวิตประจำวันในการเทรด ก่อนโอนเงินควรตรวจสอบชื่อนิติบุคคลผู้ให้บริการ หน่วยงานกำกับดูแล เลขใบอนุญาต เงื่อนไขบัญชี และค่าธรรมเนียมทั้งหมดด้วยตัวเอง เนื้อหาในหน้านี้จัดทำเพื่อการเรียนรู้ ไม่ใช่คำแนะนำการลงทุน',
  homeTeaser: {
    eyebrow: 'ก่อนเปิดบัญชี',
    title: 'เปรียบเทียบโบรกเกอร์ Forex ก่อนเปิดบัญชี',
    desc: 'ศึกษาข้อมูลเกี่ยวกับบัญชี แพลตฟอร์ม ค่าใช้จ่าย และเงื่อนไขของโบรกเกอร์ก่อนตัดสินใจ',
    benefitsTitle: 'ทำไมควรเทียบให้ครบก่อนเลือก',
    benefits: [
      {
        icon: 'ri-money-dollar-circle-line',
        title: 'เห็นต้นทุนจริงก่อนเทรด',
        desc: 'เทียบสเปรด ค่าคอมมิชชัน และค่าถือสถานะข้ามคืน ไม่ต้องเดาจากตัวเลขในโฆษณา',
      },
      {
        icon: 'ri-shield-check-line',
        title: 'รู้เงื่อนไขก่อนโอนเงิน',
        desc: 'ตรวจช่องทางฝากถอน ยอดขั้นต่ำ และค่าธรรมเนียมที่อาจแอบแฝงก่อนสมัคร',
      },
      {
        icon: 'ri-layout-masonry-line',
        title: 'เลือกแพลตฟอร์มที่ใช้ถนัด',
        desc: 'เริ่มจากเครื่องมือที่คุ้นมือ ใช้ต่อได้ทั้งบัญชีทดลองและบัญชีจริง',
      },
    ],
    cta: 'ดูการเปรียบเทียบโบรกเกอร์',
    path: '/forex-trading#brokers',
    note: 'หน้านี้เป็นเนื้อหาเพื่อการศึกษาและอาจมีลิงก์พันธมิตร การเทรด Forex มีความเสี่ยงสูง',
  },
};

export const brokerFilter = {
  title: 'ตัวกรองเปรียบเทียบ',
  hint: 'เลือกสิ่งที่คุณต้องใช้ก่อน แล้วดูว่าโบรกเกอร์ใดตรงเงื่อนไขบ้าง',
  groupTitles: {
    platform: 'แพลตฟอร์มการเทรด',
    cost: 'รูปแบบต้นทุน',
  },
  platformOptions: [
    { value: 'all', label: 'ทั้งหมด' },
    { value: 'mt4mt5', label: 'MT4 + MT5' },
    { value: 'own-platform', label: 'มีแพลตฟอร์มของโบรกเกอร์เอง' },
  ] as BrokerFilterOption[],
  costOptions: [
    { value: 'all', label: 'ทั้งหมด' },
    { value: 'spread', label: 'รวมต้นทุนในสเปรด' },
    { value: 'commission', label: 'ค่าคอมมิชชันแยก' },
  ] as BrokerFilterOption[],
  resultPrefix: 'แสดง',
  resultMiddle: 'จากทั้งหมด',
  resultSuffix: 'โบรกเกอร์',
  resetLabel: 'ล้างตัวกรอง',
  emptyTitle: 'ยังไม่มีโบรกเกอร์ที่ตรงกับเงื่อนไขนี้',
  emptyText:
    'ลองกลับไปเลือก «ทั้งหมด» ในหัวข้อที่เพิ่งกรอง แล้วค่อยเทียบทีละหัวข้อ เพราะรายชื่อโบรกเกอร์ในหน้านี้มีไม่กี่ราย',
  costHint:
    'ทั้งสามโบรกเกอร์มีบัญชีทั้งแบบรวมสเปรดและแบบคิดค่าคอมมิชชัน ตัวกรองนี้จึงใช้เน้นบรรทัดต้นทุนที่คุณสนใจ ไม่ได้ตัดรายใดออก',
  costBadge: 'มีบัญชีแบบนี้',
  pendingFieldNote: 'รายละเอียดเชิงตัวเลขยังรอยืนยัน',
};

export const brokerFab = {
  label: 'เปิดบัญชี',
  ariaOpen: 'เปิดรายชื่อโบรกเกอร์',
  ariaClose: 'ปิดรายชื่อโบรกเกอร์',
  panelTitle: 'เลือกโบรกเกอร์',
  panelSubtitle: 'เลือกชื่อที่อยากอ่านก่อน แล้วค่อยเปิดรายละเอียดแต่ละหัวข้อ',
  detailLink: 'ดูรายละเอียด',
  allLink: 'ดูการเปรียบเทียบทั้งหมด',
  pendingNote: 'ตัวเลขบางช่องยังรอยืนยัน',
  close: 'ปิด',
  countUnit: 'แห่ง',
  countHint: 'โบรกเกอร์ที่เทียบได้',
};

export const brokers: Broker[] = [
  {
    id: 'xm',
    anchor: 'broker-xm',
    name: 'XM',
    mark: 'XM',
    logo: '/2.jpg',
    tagline: 'ชื่อที่มือใหม่มักเจอตอนเริ่มหาข้อมูล',
    summary:
      'XM เป็นชื่อที่คนเริ่มเทรดมักเจอตอนหาคู่มือและเปิดบัญชีทดลอง สิ่งที่ควรถามให้ชัดก่อนสมัครคือบัญชีของคุณจะถูกเปิดภายใต้นิติบุคคลใดในภูมิภาคของคุณ เพราะเงื่อนไขบัญชีและหน่วยงานกำกับดูแลอาจไม่เหมือนกันในแต่ละประเทศ',
    platformTypes: ['mt4mt5'],
    costModels: ['spread', 'commission'],
    card: {
      platforms: 'MT4 · MT5',
      accounts: 'บัญชีมาตรฐาน และบัญชีที่คิดค่าคอมมิชชัน',
      cost: 'สเปรดและค่าถือสถานะข้ามคืน (รอยืนยันตัวเลข)',
      funding: 'ธนาคารในประเทศ · e-wallet (รอยืนยันช่องทาง)',
    },
    detail: {
      brand: [
        'XM ถูกพูดถึงบ่อยในกลุ่มคนที่เพิ่งเริ่มเทรด เพราะมีเนื้อหาประกอบการเรียนและบัญชีทดลองให้ฝึกวางแผนก่อนใช้เงินจริง ตัวเว็บไซต์มีหลายภาษา ทำให้อ่านเงื่อนไขเบื้องต้นได้ง่ายขึ้นในระดับหนึ่ง',
        'แต่สิ่งที่ต้องแยกให้ออกคือชื่อแบรนด์เดียวกันอาจให้บริการผ่านคนละนิติบุคคลในแต่ละภูมิภาค เงื่อนไขบัญชี ขนาดล็อตขั้นต่ำ และช่องทางฝากถอนจึงไม่จำเป็นต้องเหมือนกัน ก่อนโอนเงินควรเปิดหน้าเงื่อนไขของประเทศคุณเองแล้วอ่านให้จบ',
      ],
      regulator: {
        label: 'นิติบุคคลและหน่วยงานกำกับดูแล',
        value:
          'รอยืนยัน — ต้องเปิดหน้าด้านล่างของเว็บไซต์ทางการเพื่อดูชื่อนิติบุคคลที่คุณจะเปิดบัญชีด้วย ประเทศที่จดทะเบียน และเลขใบอนุญาต แล้วนำเลขนั้นไปค้นในทะเบียนของหน่วยงานกำกับดูแลอีกครั้ง',
        pending: true,
      },
      entities: [
        'ชื่อนิติบุคคลที่ระบุบนสัญญาเปิดบัญชี',
        'ประเทศที่จดทะเบียนและหน่วยงานที่กำกับดูแล',
        'เลขใบอนุญาตที่ค้นหาได้ในทะเบียนของหน่วยงานนั้น',
        'ที่อยู่ติดต่อและช่องทางยื่นเรื่องร้องเรียน',
      ],
      accounts:
        'หน้าเว็บของ XM แยกบัญชีออกเป็นหลายระดับ ตั้งแต่บัญชีที่รวมต้นทุนไว้ในสเปรดไปจนถึงบัญชีที่คิดค่าคอมมิชชันแยก ตัวเลขเงินฝากขั้นต่ำ ขนาดล็อตขั้นต่ำ และเลเวอเรจสูงสุดให้ยึดจากหน้าสเปกบัญชีของภูมิภาคคุณ — รอยืนยันก่อนสมัคร',
      platforms:
        'MT4 และ MT5 ทั้งบนเดสก์ท็อปและแอปมือถือ — รอยืนยันอีกครั้งว่าบัญชีที่คุณเลือกใช้ได้กับแพลตฟอร์มใดบ้าง และมีเครื่องมือจัดการความเสี่ยงครบตามที่ต้องการหรือไม่',
      cost:
        'ต้นทุนหลักคือสเปรดและค่าถือสถานะข้ามคืน ส่วนบัญชีแบบคิดค่าคอมมิชชันจะแยกค่ารายล็อตออกมาให้เห็นชัด ตัวเลขสเปรดที่ควรใช้ตัดสินใจคือค่าที่เห็นในแพลตฟอร์มตอนตลาดเปิด ไม่ใช่ตัวเลขในโฆษณา — รอยืนยันอัตราจริง',
      funding:
        'รองรับการโอนผ่านธนาคารและกระเป๋าเงินอิเล็กทรอนิกส์หลายเจ้า เวลาดำเนินการขึ้นกับธนาคารต้นทางเป็นหลัก — รอยืนยันช่องทางที่ใช้ได้จริงในประเทศคุณ ค่าธรรมเนียม และยอดถอนขั้นต่ำ',
      cautions: [
        'อ่านเงื่อนไขของสิทธิ์ส่งเสริมการขายและข้อผูกพันการเทรดให้จบก่อนรับสิทธิ์ใด ๆ',
        'ตรวจว่าบัญชีของคุณอยู่ภายใต้เอนทิตีใด และมีหน่วยงานใดกำกับดูแลอย่างชัดเจน',
        'ทดลองฝากและถอนจำนวนเล็กน้อยให้ครบรอบก่อนใช้งานจริง',
      ],
    },
    officialUrl: 'https://www.xm.com',
    /* @replace:broker-xm */
    accountUrl: 'https://clicks.pipaffiliates.com/c?c=1331227&l=th&p=1',
  },
  {
    id: 'exness',
    anchor: 'broker-exness',
    name: 'Exness',
    mark: 'EX',
    logo: '/1.jpg',
    tagline: 'จุดเด่นอยู่ที่การแยกต้นทุนให้เห็นชัด',
    summary:
      'Exness แยกประเภทบัญชีตามลักษณะต้นทุน คือบัญชีที่รวมค่าใช้จ่ายไว้ในสเปรด และบัญชีแบบ Raw Spread ที่คิดค่าคอมมิชชันต่อล็อตแยกออกมา ทำให้เปรียบเทียบต้นทุนต่อไม้ได้ตรงไปตรงมา แต่ต้องบวกทั้งสองส่วนเข้าด้วยกันทุกครั้ง',
    platformTypes: ['mt4mt5', 'own-platform'],
    costModels: ['spread', 'commission'],
    card: {
      platforms: 'MT4 · MT5 · Exness Terminal',
      accounts: 'Standard · Raw Spread',
      cost: 'ค่าคอมมิชชันต่อล็อต + สเปรด (รอยืนยันตัวเลข)',
      funding: 'หลายช่องทาง · ประมวลผลอัตโนมัติ (รอยืนยัน)',
    },
    detail: {
      brand: [
        'Exness วางตำแหน่งบัญชีไว้หลายแบบ ตั้งแต่บัญชีที่รวมต้นทุนไว้ในสเปรด ไปจนถึงบัญชี Raw Spread ที่คิดค่าคอมมิชชันแยกเป็นรายล็อต จึงเหมาะกับคนที่ต้องการเห็นโครงสร้างต้นทุนชัด ๆ แล้วคำนวณเองว่าคุ้มกับสไตล์การเทรดของตนหรือไม่',
        'จุดที่คนมักข้ามคือเงื่อนไขการถอนขั้นต่ำ ช่องทางที่ใช้ได้จริงในประเทศของคุณ และสกุลเงินของบัญชี ซึ่งมีผลกับค่าคอนเวอร์ชันตอนฝากและถอน ทางที่ดีคือทดลองฝากจำนวนเล็กน้อยแล้วถอนออกให้ครบรอบก่อนใช้งานจริง',
      ],
      regulator: {
        label: 'นิติบุคคลและหน่วยงานกำกับดูแล',
        value:
          'รอยืนยัน — ตรวจชื่อนิติบุคคลที่ระบุในสัญญาเปิดบัญชี ประเทศที่จดทะเบียน และเลขใบอนุญาตจากหน้าด้านล่างของเว็บไซต์ทางการ ก่อนนำไปค้นในทะเบียนของหน่วยงานกำกับดูแล',
        pending: true,
      },
      entities: [
        'นิติบุคคลที่ให้บริการลูกค้าในประเทศของคุณ',
        'ประเทศที่จดทะเบียนและหน่วยงานกำกับดูแล',
        'เลขใบอนุญาตและขอบเขตผลิตภัณฑ์ที่ได้รับอนุญาต',
        'ช่องทางติดต่อทางการเมื่อเกิดข้อพิพาท',
      ],
      accounts:
        'บัญชี Standard เหมาะกับการดูต้นทุนรวมในสเปรดเพียงตัวเดียว ส่วนบัญชี Raw Spread แยกค่าคอมมิชชันต่อล็อตออกมา ตัวเลขที่ควรเทียบคือสเปรดเฉลี่ย ค่าคอมมิชชันไป-กลับ และเงินฝากขั้นต่ำของแต่ละบัญชี ซึ่งต้องอ่านจากหน้าสเปกบัญชีจริง — รอยืนยัน',
      platforms:
        'MT4, MT5 และเทอร์มินัลของ Exness เอง — รอยืนยันความต่างของแต่ละแพลตฟอร์ม โดยเฉพาะเครื่องมือคำนวณความเสี่ยงและคำสั่งที่ใช้ประจำ',
      cost:
        'บัญชี Raw Spread มีค่าคอมมิชชันต่อล็อตที่ชัดเจน ทำให้คำนวณต้นทุนต่อไม้ได้ง่ายกว่า แต่ต้องบวกสเปรดเข้าไปด้วยทุกครั้ง ไม่ใช่ดูแค่ค่าคอมมิชชัน และอย่าลืมค่าถือสถานะข้ามคืนหากถือข้ามวัน — รอยืนยันตัวเลขจากหน้าสเปกบัญชี',
      funding:
        'มีช่องทางฝากถอนหลายรูปแบบและมักประมวลผลอัตโนมัติ แต่เวลาจริงขึ้นกับช่องทางและธนาคารต้นทาง — รอยืนยันค่าธรรมเนียม ยอดถอนขั้นต่ำ และสกุลเงินที่รองรับในบัญชีของคุณ',
      cautions: [
        'บัญชี Raw Spread ต้องบวกค่าคอมมิชชันเข้าไปในการคำนวณต้นทุนทุกไม้',
        'ตรวจสกุลเงินของบัญชี เพราะมีผลกับค่าคอนเวอร์ชันตอนฝากและถอน',
        'เลเวอเรจที่ปรับได้สูงทำให้ขาดทุนเร็วขึ้นเมื่อทิศทางผิด ควรกำหนดความเสี่ยงต่อไม้ให้ชัด',
      ],
    },
    officialUrl: 'https://www.exness.com',
    /* @replace:broker-exness */
    accountUrl: 'https://one.exnessonelink.com/boarding/sign-up/a/16004220?lng=th',
  },
  {
    id: 'fxpro',
    anchor: 'broker-fxpro',
    name: 'FxPro',
    mark: 'FP',
    logo: '/7.jpg',
    tagline: 'เน้นการเลือกเครื่องมือเทรดให้ตรงสไตล์',
    summary:
      'FxPro ให้ความสำคัญกับเครื่องมือเทรดหลายรูปแบบ ทั้ง MT4, MT5 และแพลตฟอร์มของตัวเอง เหมาะกับคนที่อยากเทียบหน้าจอก่อนเลือกใช้เป็นหลัก แต่ค่าธรรมเนียมบางรายการไม่ได้อยู่ในหน้าแรก ต้องเข้าไปเปิดตารางค่าธรรมเนียมเอง',
    platformTypes: ['mt4mt5', 'own-platform'],
    costModels: ['spread', 'commission'],
    card: {
      platforms: 'MT4 · MT5 · FxPro Platform',
      accounts: 'Standard · บัญชีที่คิดค่าคอมมิชชัน',
      cost: 'สเปรด + ค่าถือสถานะข้ามคืน (รอยืนยันตัวเลข)',
      funding: 'บัตร · e-wallet (รอยืนยันรายละเอียด)',
    },
    detail: {
      brand: [
        'FxPro ให้ความสำคัญกับเครื่องมือเทรดหลายรูปแบบ ทั้ง MT4, MT5 และแพลตฟอร์มของตัวเอง เหมาะกับคนที่อยากลองเทียบหน้าจอก่อนตัดสินใจว่าจะใช้ตัวไหนเป็นหลัก ไม่ได้เลือกจากโปรโมชันเพียงอย่างเดียว',
        'ข้อควรระวังคือค่าธรรมเนียมบางประเภท เช่น ค่าถือสถานะข้ามคืนหรือค่าฝากถอน อาจไม่ได้อยู่ในหน้าแรกของเว็บไซต์ ต้องเข้าไปดูในส่วนเงื่อนไขการเทรดและตารางค่าธรรมเนียมก่อนทุกครั้ง',
      ],
      regulator: {
        label: 'นิติบุคคลและหน่วยงานกำกับดูแล',
        value:
          'รอยืนยัน — FxPro ให้บริการผ่านหลายนิติบุคคลในแต่ละภูมิภาค ต้องตรวจว่าบัญชีของคุณจะเปิดกับนิติบุคคลใด มีเลขใบอนุญาตอะไร และอยู่ภายใต้หน่วยงานกำกับดูแลใดจากหน้าด้านล่างของเว็บไซต์ทางการ',
        pending: true,
      },
      entities: [
        'นิติบุคคลที่ให้บริการในประเทศของคุณ',
        'เลขใบอนุญาตและหน่วยงานกำกับดูแล',
        'ขอบเขตผลิตภัณฑ์ที่นิติบุคคลนั้นได้รับอนุญาต',
        'ที่อยู่จดทะเบียนและช่องทางติดต่อทางการ',
      ],
      accounts:
        'FxPro แยกบัญชีตามรูปแบบต้นทุนเช่นกัน ความต่างหลักอยู่ที่คุณจ่ายผ่านสเปรดหรือจ่ายเป็นค่าคอมมิชชันแยก และมีเงื่อนไขเงินฝากขั้นต่ำต่างกันในแต่ละบัญชี — รอยืนยันตัวเลขทั้งหมดจากหน้าสเปกบัญชีก่อนเปิดบัญชี',
      platforms:
        'MT4, MT5 และแพลตฟอร์มของ FxPro — รอยืนยันจำนวนเครื่องมือและตลาดที่เปิดให้เทรดในบัญชีของคุณ เพราะแต่ละแพลตฟอร์มอาจมีชุดคำสั่งและตัวชี้วัดไม่เท่ากัน',
      cost:
        'นอกจากสเปรดและค่าคอมมิชชัน ให้ดูค่าถือสถานะข้ามคืนและค่าที่อาจเกิดกับช่องทางฝากถอนที่คุณใช้จริง ต้นทุนเล็ก ๆ เหล่านี้สะสมได้มากกว่าที่คิดเมื่อเทรดบ่อย — รอยืนยันตัวเลขทั้งหมด',
      funding:
        'ใช้บัตรและกระเป๋าเงินอิเล็กทรอนิกส์เป็นหลัก ควรตรวจว่าช่องทางที่คุณใช้รองรับการถอนกลับไปยังแหล่งเดิมหรือไม่ และมีค่าธรรมเนียมหรือระยะเวลารอเท่าไร — รอยืนยันรายละเอียดกับเว็บไซต์ทางการ',
      cautions: [
        'ค่าธรรมเนียมบางรายการไม่ได้อยู่หน้าแรก ต้องเปิดหน้าตารางค่าธรรมเนียมทุกครั้ง',
        'ตรวจเลขใบอนุญาตของนิติบุคคลที่คุณจะเปิดบัญชีจริง ไม่ใช่ของภูมิภาคอื่น',
        'แพลตฟอร์มที่ต่างกันอาจมีเครื่องมือไม่เท่ากัน ควรเลือกให้ตรงกับวิธีเทรดที่ใช้',
      ],
    },
    officialUrl: 'https://www.fxpro.com',
    /* @replace:broker-fxpro */
    accountUrl: 'https://direct.fxpro.group/th/partner/11378220',
  },
];